import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import './PdfViewer.scss';
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerProps = 
  | { file: string; fileList?: never; onError?: (error: Error) => void }
  | { file?: never; fileList: string[]; onError?: (error: Error) => void };

const PdfViewer: React.FC<PdfViewerProps> = ({ file, fileList, onError }) => {

  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfBlobUrls, setPdfBlobUrls] = useState<string[]>([]);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700)
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (file) {
      const byteCharacters = atob(file);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      setPdfBlobUrls([URL.createObjectURL(blob)]);
    } else if (fileList) {

      const urls = fileList.map((fileString) => {
          const byteCharacters = atob(fileString);
          const byteArray = new Uint8Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          return URL.createObjectURL(blob);
      });
      setPdfBlobUrls(urls);
    }

    const handleResize = () => {
      const newScale = ((file && (numPages??0) > 1) || (fileList && fileList.length > 1)) ?
                          (window.innerWidth < 630 ? (window.innerWidth < 565 ? (window.innerWidth < 510 ? (window.innerWidth < 450 ? 0.47 : 0.6) : 0.7) : 0.8) : 0.9)
                          :
                          (window.innerWidth < 630 ? (window.innerWidth < 565 ? (window.innerWidth < 510 ? (window.innerWidth < 450 ? 0.57 : 0.7) : 0.8) : 0.9) : 1);
      setScale(newScale);
      setIsMobile(window.innerWidth <= 700)
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handleNextPdf = () => {
    if (currentPdfIndex < pdfBlobUrls.length - 1) {
      setCurrentPdfIndex(currentPdfIndex + 1);
      setNumPages(null);
    }
  };

  const handlePreviousPdf = () => {
    if (currentPdfIndex > 0) {
      setCurrentPdfIndex(currentPdfIndex - 1);
      setNumPages(null);
    }
  };

  return (
    <div className="pdf-viewer">
      {file && (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {(numPages??0) > 1 ? <div style={{ marginTop: isMobile ? '150px' : '250px' }}>
            <button
              className="circular-button"
              onClick={() => setPageNumber(pageNumber - 1)}
              disabled={pageNumber <= 1}
            >
              &lt;
            </button>
          </div> : null}
          <div ref={containerRef} className={`pdf-document ${isMobile ? "mobile" : ""}`}>
            <Document
              file={pdfBlobUrls[0]}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onError}
            >
              <Page
                pageNumber={pageNumber}
                width={containerWidth ?? undefined}
                scale={containerWidth ? undefined : scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
          {(numPages??0) > 1 ? <div style={{ marginTop: isMobile ? '150px' : '250px' }}>
            <button
              className="circular-button"
              onClick={() => setPageNumber(pageNumber + 1)}
              disabled={pageNumber >= numPages!}
            >
              &gt;
            </button>
          </div> : null}
        </div>
      )}

      {fileList && (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {fileList.length > 1 ? <div style={{ marginTop: isMobile ? '150px' : '250px' }}>
            <button
              className="circular-button"
              onClick={handlePreviousPdf}
              disabled={currentPdfIndex <= 0}
            >
              &lt;
            </button>
          </div> : null}
          <Document
            className={`pdf-document ${isMobile ? "mobile" : ""}`}
            file={pdfBlobUrls[currentPdfIndex]}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onError}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} renderTextLayer={false} renderAnnotationLayer={false} scale={scale}/>
            ))}
          </Document>
          {fileList.length > 1 ? <div style={{ marginTop: isMobile ? '150px' : '250px' }}>
            <button
              className="circular-button"
              onClick={handleNextPdf}
              disabled={currentPdfIndex >= pdfBlobUrls.length - 1}
            >
              &gt;
            </button>
          </div> : null}
        </div>
      )}

      {(file && (numPages??0) > 1) || (fileList && fileList.length > 1)? <div style={{ marginTop: '5px', textAlign: 'center' }}>
        <span style={{ fontSize: '12px', backgroundColor: '#B27668', color: 'white', padding: '4px 25px', borderRadius: '4px' }}>
          {file ? `Página ${pageNumber} de ${numPages}` : `Documento ${currentPdfIndex + 1} de ${pdfBlobUrls.length}`}
        </span>
      </div> : null}
    </div>
  );
};

export default PdfViewer;
